// FILE: src/components/Navbar/Navbar.js
import React from "react";
import { Link, useLocation } from "react-router-dom";
import DATA from "../../data";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import styles from "./Navbar.module.css";

const mailto = `mailto:${DATA.company.email}?subject=${encodeURIComponent(
  "Project inquiry"
)}&body=${encodeURIComponent("Tell us about your project and timeline:")}`;

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState("home");
  const location = useLocation();
  const logo = "/assets/images/TekiMart-Logo.png";
  React.useEffect(() => setOpen(false), [location.pathname]);

  // Smooth in-page anchor scroll
  React.useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest('a[data-anchor="1"]');
      if (!a) return;
      const id = a.getAttribute("href").replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `/#${id}`);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Scroll spy (highlight active section) — only on Home
  React.useEffect(() => {
    if (location.pathname !== "/") return;
    const ids = DATA.nav.map((n) => n.id);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        // favor elements near the center of the viewport
        root: null,
        rootMargin: "-40% 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [location.pathname]);

  const onHome = location.pathname === "/";

  return (
    <nav className={`navbar navbar-expand-lg bg-surface border-bottom sticky-top ${styles.nav}`}>
      {/* Accessible skip link */}
      <a href="#home" className="skip-link">Skip to content</a>

      <div className="container">
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <img src={logo} alt="TekiMart logo" className={`me-2 ${styles.logo}`} />
          <b className="brand-accent">TekiMart</b>
          <span className="ms-2 badge rounded-pill text-bg-soft">Since 2015</span>
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          aria-expanded={open}
          aria-label="Toggle navigation"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse ${open ? "show" : ""}`}>
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
            {DATA.nav.map((n) => {
              const isActive = onHome && active === n.id;
              return (
                <li className="nav-item" key={n.id}>
                  {onHome ? (
                    <a
                      className={`nav-link ${isActive ? "active" : ""}`}
                      href={`#${n.id}`}
                      data-anchor="1"
                      aria-current={isActive ? "page" : undefined}
                    >
                      {n.label}
                      <span className={styles.activeBar} aria-hidden="true" />
                    </a>
                  ) : (
                    <Link className="nav-link" to={`/#${n.id}`}>
                      {n.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          <a href={mailto} className="btn btn-sm btn-brand ms-lg-3">Get a quote</a>
          <ThemeToggle />
        </div>
      </div>
    </nav>
  );
}
